package gr.tech.site.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.functions.invert
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.styles.Theme
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text

val partnerLogoStyle = CssStyle {
    base {
        Modifier
            .flexShrink(1)
            .width(autoLength)
            .minWidth(0.px)
            .maxWidth(240.px)
            .height(160.px)
            .objectFit(ObjectFit.Contain)
            .display(DisplayStyle.Block)
            .margin(leftRight = 12.px)
            .transition(Transition.all(250.ms))
            .textAlign(TextAlign.Center)
            .fontSize(0.8.cssRem)
    }
    hover {
        Modifier
            .scale(1.10)
    }
}

fun extractCompanyName(alt: String): String {
    // Remove " Logo" suffix from alt text to get company name
    return alt.replace(" Logo", "").trim()
}

@Composable
fun PartnerLogo(
    src: String,
    alt: String,
    mod: Modifier = Modifier
) {
    Img(
        src = src,
        alt = alt,
        attrs = partnerLogoStyle.toModifier()
            .then(mod)
            .toAttrs()
    )
}

val partnerCardHeaderStyle = CssStyle {
    base {
        Modifier
            .fontSize(1.5.cssRem)
            .fontWeight(500)
            .margin(topBottom = 12.px)
            .textAlign(TextAlign.Center)
            .color(Theme.DarkGreen.rgb)
    }
}

@Composable
fun PartnerCardWithHeader(
    src: String,
    alt: String,
    mod: Modifier = Modifier
) {
    Div(Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).classNames("align-items-center").toAttrs()) {
        PartnerLogo(src = src, alt = alt, mod = mod)
        H3(partnerCardHeaderStyle.toModifier().toAttrs()) {
            Text(extractCompanyName(alt))
        }
    }
}

@Composable
fun PartnersSectionWithHeaders() {
    // Responsive wrapping flexbox that adapts from 4-5 columns on desktop to fewer on mobile
    Div(
        Modifier
            .width(100.percent)
            .display(DisplayStyle.Flex)
            .flexWrap(FlexWrap.Wrap)
            .classNames("justify-content-around")
            .gap(2.cssRem)
            .padding(1.cssRem)
            .toAttrs()
    ) {
        // BFG Supply
        PartnerCardWithHeader(src = "/logos/BFGSupplyLogo.png", alt = "BFG Supply Logo")
        // Growers Choice
        PartnerCardWithHeader(src = "/logos/GrowersChoiceLogo.png", alt = "Growers Choice Logo")
        // Anden
        PartnerCardWithHeader(src = "/logos/AndenLogo.png", alt = "Anden Logo")
        // Think Grow
        PartnerCardWithHeader(src = "/logos/ThinkGrowLogo.png", alt = "Think Grow Logo")
        // Quest
        PartnerCardWithHeader(src = "/logos/QuestLogo.png", alt = "Quest Logo")

        // DL Wholesale
        PartnerCardWithHeader(src = "/logos/DLWholesaleLogo.jpg", alt = "DL Wholesale Logo")
        // TrolMaster
        PartnerCardWithHeader(src = "/logos/TrolMasterLogo.webp", alt = "TrolMaster Logo")
        // Mango Tech
        PartnerCardWithHeader(src = "/logos/MangoTechLogo.png", alt = "Mango Tech Logo")
        // GK Lighting
        PartnerCardWithHeader(src = "/logos/GKLogo.png", alt = "GK Lighting Logo", mod = Modifier.filter(Filter.of(invert(100.percent))))
        // B Lite
        PartnerCardWithHeader(src = "/logos/BLiteLogo.png", alt = "B Lite Logo")

        // Gavita
        PartnerCardWithHeader(src = "/logos/GavitaLogo.jpeg", alt = "Gavita Logo")
        // Yenjie
        PartnerCardWithHeader(src = "/logos/YenjieLogo.jpg", alt = "Yenjie Logo")
        // Flexstar
        PartnerCardWithHeader(src = "/logos/FlexstarLogo.png", alt = "Flexstar Logo", mod = Modifier.filter(Filter.of(invert(100.percent))))
        // HLG
        PartnerCardWithHeader(src = "/logos/HLGLogo.png", alt = "Horticulture Lighting Group Logo")
        // Volt
        PartnerCardWithHeader(src = "/logos/VoltLogo.png", alt = "Volt Logo")

        // GrowPros
        PartnerCardWithHeader(src = "/logos/GrowprosLogo.png", alt = "GrowPros Logo")
        // MammothLED
        PartnerCardWithHeader(src = "/logos/MammothLEDLogo.png", alt = "MammothLED Logo")
        // Faven
        PartnerCardWithHeader(src = "/logos/FavenLogo.png", alt = "Faven Logo")
        // FloraFlex
        PartnerCardWithHeader(src = "/logos/FloraFlex.png", alt = "FloraFlex Logo")
        // California Lightworks
        PartnerCardWithHeader(src = "/logos/CLWLogo.png", alt = "California Lightworks Logo")

        // Daikin
        PartnerCardWithHeader(src = "/logos/DaikinLogo.png", alt = "Daikin Logo")
        // Fluence
        PartnerCardWithHeader(src = "/logos/FluenceLogo.png", alt = "Fluence Logo")
        // ICYLeds
        PartnerCardWithHeader(src = "/logos/IcyLogo.png", alt = "ICYLeds Logo")
        // Lighthouse Wholesale
        PartnerCardWithHeader(src = "/logos/LighthouseLogo.png", alt = "Lighthouse Wholesale Logo")
        // DAB Pumps
        PartnerCardWithHeader(src = "/logos/DABPumpsLogo.png", alt = "DAB Pumps Logo")

        // Enlite Energy
        PartnerCardWithHeader(src = "/logos/EnliteLogo.jpg", alt = "Enlite Energy Logo")
    }
}
